import { useLayoutPreference } from '@/components/InvoiceReportDetailDrawer/hooks/useLayoutPreference';
import { useApprovalDrawerViewOptimizationFlag } from '@/hooks/feature_flag/useApprovalDrawerViewOptimizationFlag';

export const JournalLayouts = {
  table: 'table',
  card: 'card',
} as const;

export type JournalLayout =
  (typeof JournalLayouts)[keyof typeof JournalLayouts];

const DEFAULT_LAYOUT: JournalLayout = JournalLayouts.table;

const STORAGE_KEY = 'paymentDetailDrawerJournalLayout:v1';

/**
 * Tracks the 仕訳 layout choice, persisted per user in localStorage
 * (NA-5531): the last-used layout is restored the next time the user
 * reopens the drawer or reloads the page. Users who never changed the
 * layout keep seeing the default (`table`). Mirrors `useTransactionsLayout`.
 *
 * Gated by the same `approval-drawer-view-optimization` flag as the
 * Transactions section — this is one flag covering the whole "approval
 * drawer view optimization" initiative, not a per-section flag. The clamp
 * lives here rather than at the call site so a caller cannot forget it: with
 * the flag off the layout is always `table`, even if the user switched to
 * `card` earlier in the same session (or a previous one) while the flag was on.
 */
export const useJournalLayout = () => {
  const isLayoutSwitchEnabled = useApprovalDrawerViewOptimizationFlag();

  const { layout, setLayout } = useLayoutPreference<JournalLayout>({
    storageKey: STORAGE_KEY,
    defaultLayout: DEFAULT_LAYOUT,
    validValues: Object.values(JournalLayouts),
    isEnabled: isLayoutSwitchEnabled,
  });

  return {
    layout,
    setLayout,
    isLayoutSwitchEnabled,
  };
};
